import { v4 as uuidv4 } from 'uuid'

// Generate unique ID
export function generateId(): string {
  return uuidv4()
}

// Calculate distance between two coordinates (Haversine formula)
export function calculateDistance(
  lat1: number,
  lon1: number,
  lat2: number,
  lon2: number
): number {
  const R = 3959 // Earth's radius in miles
  const dLat = toRad(lat2 - lat1)
  const dLon = toRad(lon2 - lon1)
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) *
    Math.sin(dLon / 2) * Math.sin(dLon / 2)
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a))
  return R * c
}

function toRad(deg: number): number {
  return deg * (Math.PI / 180)
}

// Format distance for display
export function formatDistance(miles: number): string {
  if (miles < 0.1) {
    return `${Math.round(miles * 5280)} ft`
  } else if (miles < 10) {
    return `${miles.toFixed(1)} mi`
  } else {
    return `${Math.round(miles)} mi`
  }
}

// Format date for display
export function formatDate(date: string | Date): string {
  const d = new Date(date)
  return d.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  })
}

// Format time for display
export function formatTime(date: string | Date): string {
  const d = new Date(date)
  return d.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
  })
}

// Format date and time
export function formatDateTime(date: string | Date): string {
  return `${formatDate(date)} at ${formatTime(date)}`
}

// Decode a blob into a drawable source. Prefers createImageBitmap, which
// decodes once (often off the main thread) and is far more memory-robust
// on iOS Safari than `new Image()` + blob URL — the latter fails with
// "Could not load image" under memory pressure, which is exactly what
// wedged the upload pipeline when several full-res decodes overlapped.
// Falls back to the <img> path where createImageBitmap is unavailable or
// rejects. Caller MUST call close() to release the bitmap.
interface DecodedImage {
  source: CanvasImageSource
  width: number
  height: number
  close: () => void
}

async function decodeImageSource(file: File | Blob): Promise<DecodedImage> {
  if (typeof createImageBitmap === 'function') {
    try {
      // imageOrientation: 'from-image' applies the EXIF orientation tag the
      // way <img> rendering does — without it, iPhone photos carrying an
      // orientation tag would decode rotated. (Unsupported older Safari
      // rejects here → we fall through to the <img> path, which respects
      // orientation natively.)
      const bitmap = await createImageBitmap(file, { imageOrientation: 'from-image' })
      return { source: bitmap, width: bitmap.width, height: bitmap.height, close: () => bitmap.close() }
    } catch {
      // Fall through to the <img> path — some formats/edge cases reject.
    }
  }
  const img = await new Promise<HTMLImageElement>((resolve, reject) => {
    const el = new Image()
    const url = URL.createObjectURL(file)
    el.onload = () => { URL.revokeObjectURL(url); resolve(el) }
    el.onerror = () => { URL.revokeObjectURL(url); reject(new Error('Could not load image')) }
    el.src = url
  })
  return { source: img, width: img.naturalWidth, height: img.naturalHeight, close: () => {} }
}

// Draw an already-decoded source to a downscaled JPEG blob.
function drawToJpegBlob(
  source: CanvasImageSource,
  srcWidth: number,
  srcHeight: number,
  maxWidth: number,
  quality: number,
): Promise<Blob> {
  let width = srcWidth
  let height = srcHeight
  if (width > maxWidth) {
    height = (height * maxWidth) / width
    width = maxWidth
  }
  const canvas = document.createElement('canvas')
  canvas.width = width
  canvas.height = height
  const ctx = canvas.getContext('2d')
  if (!ctx) return Promise.reject(new Error('Could not get canvas context'))
  ctx.drawImage(source, 0, 0, width, height)
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => blob ? resolve(blob) : reject(new Error('Could not create blob')),
      'image/jpeg',
      quality,
    )
  })
}

// Resize image using canvas (single size). Decodes once, then draws.
export async function resizeImage(
  file: File | Blob,
  maxWidth: number,
  quality: number = 0.85
): Promise<Blob> {
  const decoded = await decodeImageSource(file)
  try {
    return await drawToJpegBlob(decoded.source, decoded.width, decoded.height, maxWidth, quality)
  } finally {
    decoded.close()
  }
}

export interface ResizeSpec { maxWidth: number; quality: number }

// Decode the source ONCE and emit several downscaled JPEGs from it. This
// is the memory-critical path for the upload pipeline: decoding a 12MP
// photo three separate times in parallel (the old behavior) is what blew
// iOS Safari's WebContent memory ceiling and produced "Could not load
// image" on a backlog of queued photos. One decode, three draws.
export async function resizeImageToSizes(
  file: File | Blob,
  specs: ResizeSpec[],
): Promise<Blob[]> {
  const decoded = await decodeImageSource(file)
  try {
    const out: Blob[] = []
    for (const spec of specs) {
      out.push(await drawToJpegBlob(decoded.source, decoded.width, decoded.height, spec.maxWidth, spec.quality))
    }
    return out
  } finally {
    decoded.close()
  }
}

// Get EXIF data from image (specifically GPS coordinates)
export async function getImageLocation(_file: File): Promise<{ latitude: number; longitude: number } | null> {
  // For now, we'll use the device's current location
  // A more complete implementation would read EXIF data
  return null
}

// Get current location
export function getCurrentLocation(): Promise<GeolocationPosition> {
  return new Promise((resolve, reject) => {
    if (!navigator.geolocation) {
      reject(new Error('Geolocation is not supported'))
      return
    }

    navigator.geolocation.getCurrentPosition(resolve, reject, {
      enableHighAccuracy: true,
      timeout: 10000,
      maximumAge: 60000,
    })
  })
}

// Debounce function
export function debounce<T extends (...args: unknown[]) => unknown>(
  func: T,
  wait: number
): (...args: Parameters<T>) => void {
  let timeout: ReturnType<typeof setTimeout> | null = null

  return function executedFunction(...args: Parameters<T>) {
    const later = () => {
      timeout = null
      func(...args)
    }

    if (timeout) {
      clearTimeout(timeout)
    }
    timeout = setTimeout(later, wait)
  }
}

// Throttle function
export function throttle<T extends (...args: unknown[]) => unknown>(
  func: T,
  limit: number
): (...args: Parameters<T>) => void {
  let inThrottle: boolean = false

  return function executedFunction(...args: Parameters<T>) {
    if (!inThrottle) {
      func(...args)
      inThrottle = true
      setTimeout(() => (inThrottle = false), limit)
    }
  }
}

// Class names helper
export function cn(...classes: (string | boolean | undefined | null)[]): string {
  return classes.filter(Boolean).join(' ')
}
